/**
 * Language Detector for vue-i18n
 *
 * Estrategia combinada de detección de idioma (pre-login):
 * 1. URL param (?lang=en) - Para emails de invitación
 * 2. localStorage (userLanguage) - De visita anterior
 * 3. navigator.language - Detección automática del navegador
 * 4. Default es - Fallback final
 */

import { isEmbedPath } from "@/utils/embedPath";

const SUPPORTED_LANGUAGES = ["es", "en", "pt-br"];
const DEFAULT_LANGUAGE = "es";

/**
 * Normaliza código de idioma a formato es/en/pt-br
 * @param {string} lang - Código de idioma (ej: ES, EN, pt-BR, pt_br)
 * @returns {string|null} - Código normalizado o null si no es válido
 */
function normalizeLanguageCode(lang) {
  if (!lang || typeof lang !== "string") return null;

  const normalized = lang.trim().toLowerCase();

  // Manejo especial para portugués
  if (normalized === "pt" || normalized.startsWith("pt-") || normalized.startsWith("pt_")) {
    return "pt-br";
  }

  return SUPPORTED_LANGUAGES.includes(normalized) ? normalized : null;
}

/**
 * Detecta idioma del navegador
 * @returns {string} - Código de idioma normalizado (es|en|pt-br) o DEFAULT
 */
function detectFromBrowser() {
  try {
    if (typeof navigator === "undefined") return null;

    const navLang = navigator.language || navigator.userLanguage;
    if (!navLang) return null;

    // Mapear browser language codes a nuestros soportados
    const browserLang = navLang.split("-")[0].toLowerCase();

    if (browserLang === "es") return "es";
    if (browserLang === "en") return "en";
    if (browserLang === "pt") return "pt-br";

    return null;
  } catch {
    return null;
  }
}

/**
 * Detecta idioma desde parámetro de URL
 * @returns {string|null} - Código normalizado o null
 */
function detectFromURL() {
  if (typeof window === "undefined") return null;

  const params = new URLSearchParams(window.location.search);
  const langParam = params.get("lang");

  return langParam ? normalizeLanguageCode(langParam) : null;
}

/**
 * Detecta idioma desde localStorage
 * @returns {string|null} - Código normalizado o null
 */
function detectFromStorage() {
  if (typeof localStorage === "undefined") return null;

  const storedLang = localStorage.getItem("userLanguage");
  return storedLang ? normalizeLanguageCode(storedLang) : null;
}

/**
 * Detecta idioma del usuario desde API (después de login)
 * Se usa en componentes de autenticación cuando usuario ya está logueado
 * @param {Object} user - Usuario obtenido del API
 * @returns {string|null} - Código normalizado o null
 */
function detectFromUser(user) {
  if (!user || !user.language) return null;
  return normalizeLanguageCode(user.language);
}

/**
 * Detecta idioma automáticamente siguiendo estrategia combinada
 * @returns {string} - Código de idioma (es|en|pt-br)
 */
export function detectLanguage() {
  // 1. Parámetro en URL (mayor prioridad para invitaciones)
  const urlLang = detectFromURL();
  if (urlLang) {
    console.debug(`[i18n] Language detected from URL param: ${urlLang}`);
    // DL-452: the embed does NOT persist the language. This module runs in module scope of
    // src/i18n/index.js, before the router exists, so without this guard the ?lang= Horizon puts
    // in the iframe src would overwrite the language stored for the user's own legacy session
    // (same origin, same localStorage).
    if (!isEmbedPath()) saveLanguage(urlLang);
    return urlLang;
  }

  // 2. localStorage (sesión anterior)
  const storageLang = detectFromStorage();
  if (storageLang) {
    console.debug(`[i18n] Language detected from localStorage: ${storageLang}`);
    return storageLang;
  }

  // 3. Navegador
  const browserLang = detectFromBrowser();
  if (browserLang) {
    console.debug(`[i18n] Language detected from browser: ${browserLang}`);
    return browserLang;
  }

  // 4. Default
  console.debug(`[i18n] Using default language: ${DEFAULT_LANGUAGE}`);
  return DEFAULT_LANGUAGE;
}

/**
 * Guarda idioma en localStorage para sesiones futuras
 * @param {string} lang - Código de idioma (es|en|pt-br)
 */
export function saveLanguage(lang) {
  if (typeof localStorage === "undefined") return;

  const normalized = normalizeLanguageCode(lang);
  if (normalized) {
    localStorage.setItem("userLanguage", normalized);
    console.debug(`[i18n] Language saved to localStorage: ${normalized}`);
  }
}

/**
 * Limpia idioma guardado en localStorage (útil para logout)
 */
export function clearLanguage() {
  if (typeof localStorage === "undefined") return;
  localStorage.removeItem("userLanguage");
  console.debug(`[i18n] Language cleared from localStorage`);
}

export {
  SUPPORTED_LANGUAGES,
  DEFAULT_LANGUAGE,
  normalizeLanguageCode,
  detectFromUser,
};
